import * as React from 'react';
import { TimeSeriesProps } from './TimeSeriesProps';
import { VictoryContainer, VictoryAxis, VictoryChart, VictoryLine, VictoryPortal, VictoryScatter } from 'victory';
import { withSize } from 'react-sizeme';
import {
  DataRow,
  dropRowsWithUndefinedAccessorResult,
  maxPrecision,
  measureDomainToRender,
  MetricComparisonColor,
  MetricsGrey,
  MetricsNeutralColor,
  undefinedToNull,
  ValueAccessor,
  VisualTableExtender,
} from '@tableau/metrics-engine';

interface SparkLineProps extends TimeSeriesProps {
  readonly size: { readonly width: number; readonly height: number };
  readonly rightMargin?: number;
  readonly strokeWidth?: number;
  readonly color?: string;
  readonly lineColor?: string;
  readonly timestampAccessor: ValueAccessor<Date | undefined>;
  readonly measureAccessor: ValueAccessor<number>;
  readonly comparisonAccessor?: ValueAccessor<number | undefined>;
  readonly formatString?: string;
  readonly shouldRenderReferenceLine?: boolean;
  readonly shouldRenderComparisonDot?: boolean;
  readonly comparisonLineStyle?: React.CSSProperties;
  readonly tableExtender?: VisualTableExtender;
  readonly colorIndicatorsEnabled?: boolean;
  readonly dotSize?: number;
  readonly haloRatio?: number;
}

/**
 * `SparkLine` renders the given data in a spark line suitable for display in list-view
 * or card style components.
 *
 * The spark line will fill provided `size` props. If you don't know the exact size, use `ResizableSparkLine`.
 *
 * If no data is provided, an empty div will be returned.
 *
 * If no stoke width is provided, the default value is `1`.
 *
 * @param props
 */
export class SparkLine extends React.Component<SparkLineProps, {}> {
  public render(): JSX.Element {
    const extendedData = this.props.tableExtender
      ? this.props.tableExtender(this.props.size.width, this.props.rightMargin ?? 0, this.props.data)
      : this.props.data;

    const currentData = dropRowsWithUndefinedAccessorResult(extendedData, this.props.measureAccessor);
    const comparisonData =
      this.props.comparisonAccessor && dropRowsWithUndefinedAccessorResult(extendedData, this.props.comparisonAccessor);

    const accessors = [this.props.measureAccessor, this.props.comparisonAccessor];
    const domain = measureDomainToRender(extendedData, maxPrecision(this.props.formatString), accessors);

    const strokeWidth = this.props.strokeWidth ?? 1;
    const comparisonLineStyle = {
      ...this.props.comparisonLineStyle,
      ...(this.props.comparisonLineStyle?.strokeWidth ? {} : { strokeWidth: strokeWidth }),
      stroke: MetricComparisonColor,
    };

    const dotSize = this.props.dotSize ?? 3;

    const latestData = this.props.data[this.props.data.length - 1];

    return (
      <div data-tb-test-id={this.props.testId || 'sparkline'} style={{ ...this.props.style }}>
        {this.props.data.length > 0 && (
          <VictoryChart
            height={this.props.size.height}
            width={this.props.size.width}
            padding={0}
            domainPadding={{ x: [0, 3], y: 3 }}
            domain={{ y: domain as [number, number] }}
            singleQuadrantDomainPadding={false}
            containerComponent={<VictoryContainer responsive={false} portalZIndex={0} />}
          >
            <VictoryAxis
              crossAxis={false}
              style={{
                tickLabels: { fill: 'none' },
                axisLabel: { fill: 'none' },
                axis: { stroke: 'none' },
              }}
              standalone={false}
              orientation={'bottom'}
            />
            <VictoryAxis
              dependentAxis
              crossAxis={false}
              style={{
                grid: { stroke: 'none' },
                tickLabels: { fill: 'none' },
                axis: { stroke: 'none' },
              }}
            />
            {this.props.shouldRenderReferenceLine && this.props.comparisonAccessor && (
              <VictoryLine
                // `data` isn't typed as readonly, hence this cast
                data={comparisonData as DataRow[]}
                x={this.props.timestampAccessor}
                y={undefinedToNull(this.props.comparisonAccessor)}
                style={{
                  data: comparisonLineStyle,
                  labels: { fill: 'none' },
                }}
              />
            )}
            {this.props.shouldRenderReferenceLine &&
              this.props.shouldRenderComparisonDot &&
              this.props.comparisonAccessor &&
              this.props.comparisonAccessor(latestData) && (
                <VictoryScatter
                  data={[latestData]}
                  style={{
                    data: { fill: MetricComparisonColor },
                  }}
                  size={dotSize}
                  // @ts-ignore: Victory doesn't allow data accessor functions to return Date or undefined. This is fixed in version 34.2.0.
                  x={this.props.timestampAccessor}
                  y={this.props.comparisonAccessor}
                  dataComponent={<ChiaSeed colorIndicatorsEnabled={this.props.colorIndicatorsEnabled} haloRatio={0} />}
                />
              )}
            <VictoryLine
              // `data` isn't typed as readonly, hence this cast
              data={currentData as DataRow[]}
              // @ts-ignore: Victory doesn't allow data accessor functions to return Date or undefined. This is fixed in version 34.2.0.
              x={this.props.timestampAccessor}
              y={undefinedToNull(this.props.measureAccessor)}
              style={{
                data: {
                  stroke:
                    this.props.lineColor ?? (this.props.colorIndicatorsEnabled ? MetricsGrey : MetricsNeutralColor),
                  strokeWidth: strokeWidth,
                },
                labels: { fill: 'none' },
              }}
            />
            <VictoryScatter
              data={[latestData]}
              style={{
                data: { fill: this.props.color ?? MetricsNeutralColor },
              }}
              size={dotSize}
              // @ts-ignore: Victory doesn't allow data accessor functions to return Date or undefined. This is fixed in version 34.2.0.
              x={this.props.timestampAccessor}
              y={this.props.measureAccessor}
              dataComponent={
                <ChiaSeed colorIndicatorsEnabled={this.props.colorIndicatorsEnabled} haloRatio={this.props.haloRatio} />
              }
            />
          </VictoryChart>
        )}
      </div>
    );
  }

  public shouldComponentUpdate(nextProps: SparkLineProps): boolean {
    return (
      this.props.data !== nextProps.data ||
      this.props.size.width !== nextProps.size.width ||
      this.props.size.height !== nextProps.size.height ||
      this.props.strokeWidth !== nextProps.strokeWidth
    );
  }
}

interface ChiaSeedProps {
  readonly size: number;
  readonly x: number;
  readonly y: number;
  readonly colorIndicatorsEnabled: boolean;
  readonly style: React.CSSProperties;
  readonly haloRatio?: number;
}

const ChiaSeed = (props: Partial<ChiaSeedProps>): React.ReactElement => {
  return (
    <VictoryPortal>
      <>
        {props.colorIndicatorsEnabled && (
          <circle
            {...{
              cx: props.x,
              cy: props.y,
              r: (props.size ?? 1) * (props.haloRatio ?? 2),
              opacity: 0.3,
              style: props.style,
            }}
          />
        )}
        <circle {...{ cx: props.x, cy: props.y, r: props.size, style: props.style }} />
      </>
    </VictoryPortal>
  );
};

/**
 * `ResizableSparkLine` renders the given data in a spark line suitable for display in list-view
 * or card style components.
 *
 * By default, the sparkline will fill whatever container it is placed in.
 *
 * If no data is provided, an empty div will be returned.
 *
 * If no stoke width is provided, the default value is `1`.
 *
 * @param props
 */
export const ResizableSparkLine = withSize({
  monitorHeight: true,
  monitorWidth: true,
})(SparkLine);
